import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import Input from "../../../components/Input";
import { TextArea } from "../../../components/TextArea";
import { SelectSingle } from "../../../components/SelectSingle";
import { MilitarSelectField } from "../../../components/MilitarSelectField";
import { api } from "../../../services/api";
import { usePermissions, PERMISSOES } from "../../../hooks/usePermissions";
import {
  Award,
  Search,
  ArrowUp,
  ArrowDown,
  Plus,
  Edit,
  Trash2,
  UserCheck,
} from "lucide-react";

interface Militar {
  id: string;
  nick: string;
  patente: {
    id: string;
    patente: string;
    abreviacao: string;
  };
}

interface MilitarHonraria {
  id: string;
  tipo: string;
  militar: Militar;
  militarPromoveu: Militar;
  criado_em: string;
}

interface HonrariaComMilitares {
  id: string;
  honraria: string;
  img: string;
  ordem: number;
  militares: {
    graCruz: MilitarHonraria[];
    comendador: MilitarHonraria[];
    cavaleiro: MilitarHonraria[];
  };
}

interface Honraria {
  id: string;
  honraria: string;
  img: string;
  descricao?: string;
  ordem: number;
}

export function GerenciarHonrarias() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<"honrarias" | "tipos">("honrarias");
  const [loading, setLoading] = useState(false);

  // Tab Honrarias
  const [honrariasComMilitares, setHonrariasComMilitares] = useState<HonrariaComMilitares[]>([]);
  const [searchHonraria, setSearchHonraria] = useState("");
  const [searchNick, setSearchNick] = useState("");

  // Tab Tipos de Honraria
  const [honrarias, setHonrarias] = useState<Honraria[]>([]);
  const [filteredHonrarias, setFilteredHonrarias] = useState<Honraria[]>([]);
  const [searchTipo, setSearchTipo] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHonraria, setEditingHonraria] = useState<Honraria | null>(null);
  const [formNome, setFormNome] = useState("");
  const [formImg, setFormImg] = useState("");
  const [formDescricao, setFormDescricao] = useState("");

  // Modal Atribuir Honraria
  const [isAtribuirModalOpen, setIsAtribuirModalOpen] = useState(false);
  const [selectedMilitarId, setSelectedMilitarId] = useState("");
  const [selectedMilitarNick, setSelectedMilitarNick] = useState("");
  const [selectedHonrariaId, setSelectedHonrariaId] = useState("");
  const [selectedTipo, setSelectedTipo] = useState("");

  // Modal Remover Honraria
  const [isRemoverModalOpen, setIsRemoverModalOpen] = useState(false);
  const [militarHonrariaToRemove, setMilitarHonrariaToRemove] = useState<{
    id: string;
    militarNick: string;
    honrariaNome: string;
  } | null>(null);

  // Modal Excluir Honraria
  const [isExcluirModalOpen, setIsExcluirModalOpen] = useState(false);
  const [honrariaToDelete, setHonrariaToDelete] = useState<{
    id: string;
    honraria: string;
  } | null>(null);

  useEffect(() => {
    if (activeTab === "honrarias") {
      loadHonrariasComMilitares();
      // Carregar lista de honrarias para o select da modal de atribuir
      if (honrarias.length === 0) {
        loadHonrarias();
      }
    } else {
      loadHonrarias();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!searchTipo.trim()) {
      setFilteredHonrarias(honrarias);
    } else {
      const filtered = honrarias.filter((h) =>
        h.honraria.toLowerCase().includes(searchTipo.toLowerCase())
      );
      setFilteredHonrarias(filtered);
    }
  }, [searchTipo, honrarias]);

  const loadHonrariasComMilitares = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchHonraria) params.append("honraria", searchHonraria);
      if (searchNick) params.append("nick", searchNick);

      const response = await api.get(`/honrarias/com-militares?${params.toString()}`);
      setHonrariasComMilitares(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar honrarias", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadHonrarias = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get("/honrarias");
      // Ordenar por ordem
      const sorted = response.data.sort((a: Honraria, b: Honraria) => a.ordem - b.ordem);
      setHonrarias(sorted);
      setFilteredHonrarias(sorted);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar honrarias", { variant: "error" });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleSearchHonrarias = (e: React.FormEvent) => {
    e.preventDefault();
    loadHonrariasComMilitares();
  };

  const handleSearchTipos = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const moveHonraria = async (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === filteredHonrarias.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const newHonrarias = [...filteredHonrarias];
    const [removed] = newHonrarias.splice(index, 1);
    newHonrarias.splice(newIndex, 0, removed);

    // Atualizar estado local
    const honrariasAtualizadas = newHonrarias.map((h, idx) => ({
      ...h,
      ordem: idx + 1,
    }));

    setFilteredHonrarias(honrariasAtualizadas);
    setHonrarias(honrariasAtualizadas);

    try {
      const ordens = honrariasAtualizadas.map((h, idx) => ({
        id: h.id,
        ordem: idx + 1,
      }));

      await api.patch("/honrarias/ordem", { ordens });
      enqueueSnackbar("Ordem atualizada com sucesso", { variant: "success" });

      await loadHonrarias(false);
    } catch (error) {
      enqueueSnackbar("Erro ao atualizar ordem", { variant: "error" });
      await loadHonrarias(false);
    }
  };

  const openCreateModal = () => {
    setEditingHonraria(null);
    setFormNome("");
    setFormImg("");
    setFormDescricao("");
    setIsModalOpen(true);
  };

  const openEditModal = (honraria: Honraria) => {
    setEditingHonraria(honraria);
    setFormNome(honraria.honraria);
    setFormImg(honraria.img);
    setFormDescricao(honraria.descricao || "");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingHonraria(null);
    setFormNome("");
    setFormImg("");
    setFormDescricao("");
  };

  const handleSaveHonraria = async () => {
    if (!formNome || !formImg) {
      enqueueSnackbar("Preencha todos os campos", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);

      if (editingHonraria) {
        await api.put(`/honrarias/${editingHonraria.id}`, {
          honraria: formNome,
          img: formImg,
          descricao: formDescricao,
        });
        enqueueSnackbar("Honraria atualizada com sucesso", { variant: "success" });
      } else {
        // Nova honraria - ordem será a próxima disponível
        const maxOrdem = Math.max(0, ...honrarias.map((h) => h.ordem));
        await api.post("/honrarias", {
          honraria: formNome,
          img: formImg,
          descricao: formDescricao,
          ordem: maxOrdem + 1,
        });
        enqueueSnackbar("Honraria criada com sucesso", { variant: "success" });
      }

      closeModal();
      loadHonrarias();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar honraria",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHonraria = async (id: string) => {
    if (!honrariaToDelete) return;

    try {
      await api.delete(`/honrarias/${id}`);
      enqueueSnackbar("Honraria excluída com sucesso", { variant: "success" });
      closeExcluirModal();
      loadHonrarias();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao excluir honraria",
        { variant: "error" }
      );
    }
  };

  const openExcluirModal = (id: string, nome: string) => {
    setHonrariaToDelete({
      id,
      honraria: nome,
    });
    setIsExcluirModalOpen(true);
  };

  const closeExcluirModal = () => {
    setIsExcluirModalOpen(false);
    setHonrariaToDelete(null);
  };

  const openAtribuirModal = () => {
    setSelectedMilitarId("");
    setSelectedMilitarNick("");
    setSelectedHonrariaId("");
    setSelectedTipo("");
    setIsAtribuirModalOpen(true);
  };

  const closeAtribuirModal = () => {
    setIsAtribuirModalOpen(false);
    setSelectedMilitarId("");
    setSelectedMilitarNick("");
    setSelectedHonrariaId("");
    setSelectedTipo("");
  };

  const handleAtribuirHonraria = async () => {
    if (!selectedMilitarId || !selectedHonrariaId || !selectedTipo) {
      enqueueSnackbar("Preencha todos os campos", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);
      await api.post("/honrarias/atribuir", {
        idMilitar: selectedMilitarId,
        idHonraria: selectedHonrariaId,
        tipo: selectedTipo,
      });
      enqueueSnackbar("Honraria atribuída com sucesso", { variant: "success" });
      closeAtribuirModal();
      loadHonrariasComMilitares();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao atribuir honraria",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const openRemoverModal = (militarHonrariaId: string, militarNick: string, honrariaNome: string) => {
    setMilitarHonrariaToRemove({
      id: militarHonrariaId,
      militarNick,
      honrariaNome,
    });
    setIsRemoverModalOpen(true);
  };

  const closeRemoverModal = () => {
    setIsRemoverModalOpen(false);
    setMilitarHonrariaToRemove(null);
  };

  const handleRemoverHonraria = async () => {
    if (!militarHonrariaToRemove) return;

    try {
      setLoading(true);
      await api.delete(`/honrarias/militar-honraria/${militarHonrariaToRemove.id}`);
      enqueueSnackbar("Honraria removida com sucesso", { variant: "success" });
      closeRemoverModal();
      loadHonrariasComMilitares();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao remover honraria",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Award className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Honrarias
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie as honrarias e suas atribuições
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "honrarias"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("honrarias")}
        >
          Honrarias
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "tipos"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("tipos")}
        >
          Tipos de Honraria
        </button>
      </div>

      {/* Tab: Honrarias */}
      {activeTab === "honrarias" && (
        <div className="space-y-6">
          {/* Filtros */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                    <Search className="w-5 h-5" />
                    Buscar Honrarias e Militares
                  </CardTitle>
                  <CardDescription>
                    Filtrar por honraria ou nick do militar
                  </CardDescription>
                </div>
                {hasPermission(PERMISSOES.ATRIBUIR_HONRARIA) && (
                  <Button
                    onClick={openAtribuirModal}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Atribuir Honraria
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearchHonrarias} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search-honraria">Honraria</Label>
                  <InputText
                    id="search-honraria"
                    name="search-honraria"
                    title=""
                    placeholder="Digite o nome da honraria..."
                    value={searchHonraria}
                    onChange={(e) => setSearchHonraria(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="search-nick">Nick do Militar</Label>
                  <InputText
                    id="search-nick"
                    name="search-nick"
                    title=""
                    placeholder="Digite o nick do militar..."
                    value={searchNick}
                    onChange={(e) => setSearchNick(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {honrariasComMilitares.length} honraria(s) encontrada(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : honrariasComMilitares.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma honraria encontrada
                </div>
              ) : (
                <div className="space-y-6">
                  {honrariasComMilitares.map((honraria) => {
                    const totalMilitares =
                      honraria.militares.graCruz.length +
                      honraria.militares.comendador.length +
                      honraria.militares.cavaleiro.length;

                    return (
                      <div key={honraria.id} className="space-y-3">
                        <div className="flex items-center gap-3 pb-2 border-b-2 border-[oklch(0.45_0.15_142)]">
                          <img
                            src={honraria.img}
                            alt={honraria.honraria}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <h3 className="font-semibold text-lg text-[oklch(0.45_0.15_142)]">
                            {honraria.honraria}
                          </h3>
                          <Badge variant="outline" className="ml-2">
                            {totalMilitares}{" "}
                            {totalMilitares === 1 ? "militar" : "militares"}
                          </Badge>
                        </div>

                        {totalMilitares === 0 ? (
                          <div className="text-sm text-muted-foreground ml-11">
                            Nenhum militar com esta honraria
                          </div>
                        ) : (
                          <div className="ml-4 space-y-4">
                            {/* Grã Cruz */}
                            {honraria.militares.graCruz.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Award className="w-4 h-4 text-[oklch(0.45_0.15_142)]" />
                                  <h4 className="font-semibold text-[oklch(0.45_0.15_142)]">
                                    Grã Cruz
                                  </h4>
                                  <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                                    {honraria.militares.graCruz.length}
                                  </Badge>
                                </div>
                                <div className="space-y-2 ml-6">
                                  {honraria.militares.graCruz.map((mh) => (
                                    <div
                                      key={mh.id}
                                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                                    >
                                      <img
                                        src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${mh.militar.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                                        alt={mh.militar.nick}
                                        className="w-12 h-12 rounded-full object-cover"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                      <div className="flex-1">
                                        <div className="font-medium">
                                          {mh.militar.patente.abreviacao} {mh.militar.nick}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Por: {mh.militarPromoveu.patente.abreviacao}{" "}
                                          {mh.militarPromoveu.nick}
                                        </div>
                                      </div>
                                      {hasPermission(PERMISSOES.REMOVER_HONRARIA) && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => openRemoverModal(mh.id, mh.militar.nick, honraria.honraria)}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Comendador */}
                            {honraria.militares.comendador.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Award className="w-4 h-4 text-[oklch(0.45_0.15_142)]" />
                                  <h4 className="font-semibold text-[oklch(0.45_0.15_142)]">
                                    Comendador
                                  </h4>
                                  <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                                    {honraria.militares.comendador.length}
                                  </Badge>
                                </div>
                                <div className="space-y-2 ml-6">
                                  {honraria.militares.comendador.map((mh) => (
                                    <div
                                      key={mh.id}
                                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                                    >
                                      <img
                                        src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${mh.militar.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                                        alt={mh.militar.nick}
                                        className="w-8 h-8 rounded-full object-cover"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                      <div className="flex-1">
                                        <div className="font-medium">
                                          {mh.militar.patente.abreviacao} {mh.militar.nick}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Por: {mh.militarPromoveu.patente.abreviacao}{" "}
                                          {mh.militarPromoveu.nick}
                                        </div>
                                      </div>
                                      {hasPermission(PERMISSOES.REMOVER_HONRARIA) && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => openRemoverModal(mh.id, mh.militar.nick, honraria.honraria)}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Cavaleiro */}
                            {honraria.militares.cavaleiro.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Award className="w-4 h-4 text-[oklch(0.45_0.15_142)]" />
                                  <h4 className="font-semibold text-[oklch(0.45_0.15_142)]">
                                    Cavaleiro
                                  </h4>
                                  <Badge className="bg-green-50 text-green-700 border-green-100 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                                    {honraria.militares.cavaleiro.length}
                                  </Badge>
                                </div>
                                <div className="space-y-2 ml-6">
                                  {honraria.militares.cavaleiro.map((mh) => (
                                    <div
                                      key={mh.id}
                                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                                    >
                                      <img
                                        src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${mh.militar.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                                        alt={mh.militar.nick}
                                        className="w-8 h-8 rounded-full object-cover"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                      <div className="flex-1">
                                        <div className="font-medium">
                                          {mh.militar.patente.abreviacao} {mh.militar.nick}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Por: {mh.militarPromoveu.patente.abreviacao}{" "}
                                          {mh.militarPromoveu.nick}
                                        </div>
                                      </div>
                                      {hasPermission(PERMISSOES.REMOVER_HONRARIA) && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => openRemoverModal(mh.id, mh.militar.nick, honraria.honraria)}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Tipos de Honraria */}
      {activeTab === "tipos" && (
        <div className="space-y-6">
          {/* Filtros */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Honrarias
              </CardTitle>
              <CardDescription>
                Filtrar por nome da honraria
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearchTipos} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search-tipo">Honraria</Label>
                  <InputText
                    id="search-tipo"
                    name="search-tipo"
                    title=""
                    placeholder="Digite o nome da honraria..."
                    value={searchTipo}
                    onChange={(e) => setSearchTipo(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  {hasPermission("criar_honraria") && (
                    <Button
                      type="button"
                      onClick={openCreateModal}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Nova Honraria
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {filteredHonrarias.length} honraria(s) encontrada(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredHonrarias.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma honraria encontrada
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredHonrarias.map((honraria, index) => (
                    <div
                      key={honraria.id}
                      className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      {/* Botões de reordenação */}
                      {!searchTipo && (
                        <div className="flex flex-col gap-1 mr-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveHonraria(index, "up")}
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveHonraria(index, "down")}
                            disabled={index === filteredHonrarias.length - 1}
                            className="h-6 w-6 p-0"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>
                      )}

                      <div className="flex items-center gap-4 flex-1">
                        <img
                          src={honraria.img}
                          alt={honraria.honraria}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {honraria.honraria}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Ordem: {honraria.ordem}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {hasPermission("editar_honraria") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(honraria)}
                            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        )}
                        {hasPermission("excluir_honraria") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openExcluirModal(honraria.id, honraria.honraria)}
                            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Criar/Editar Honraria */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingHonraria ? "Editar Honraria" : "Nova Honraria"}
              </CardTitle>
              <CardDescription>
                {editingHonraria
                  ? "Atualize os dados da honraria"
                  : "Crie uma nova honraria"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                title="Nome da Honraria"
                value={formNome}
                onChange={(e) => setFormNome(e.target.value)}
                placeholder="Ex: Ordem do Mérito"
              />
              <TextArea
                title="Descrição"
                value={formDescricao}
                onChange={(e) => setFormDescricao(e.target.value)}
                placeholder="Descrição da honraria (opcional)"
                rows={3}
              />
              <Input
                title="URL da Imagem"
                value={formImg}
                onChange={(e) => setFormImg(e.target.value)}
                placeholder="https://..."
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveHonraria}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {editingHonraria ? "Atualizar" : "Criar"}
                </Button>
                <Button onClick={closeModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Atribuir Honraria */}
      {isAtribuirModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Award className="w-5 h-5" />
                Atribuir Honraria a Militar
              </CardTitle>
              <CardDescription>
                Selecione o militar, a honraria e o tipo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <MilitarSelectField
                label="Militar"
                value={selectedMilitarId}
                onChange={(id, nick) => {
                  setSelectedMilitarId(id);
                  setSelectedMilitarNick(nick);
                }}
                placeholder="Digite o nome do militar..."
                required
              />

              <SelectSingle
                title="Honraria"
                value={selectedHonrariaId}
                onChange={(e) => setSelectedHonrariaId(e.target.value)}
                required
              >
                <option value="">Selecione uma honraria</option>
                {honrarias
                  .sort((a, b) => a.ordem - b.ordem)
                  .map((honraria) => (
                    <option key={honraria.id} value={honraria.id}>
                      {honraria.honraria}
                    </option>
                  ))}
              </SelectSingle>

              <SelectSingle
                title="Tipo"
                value={selectedTipo}
                onChange={(e) => setSelectedTipo(e.target.value)}
                required
              >
                <option value="">Selecione o tipo</option>
                <option value="Grã Cruz">Grã Cruz</option>
                <option value="Comendador">Comendador</option>
                <option value="Cavaleiro">Cavaleiro</option>
              </SelectSingle>

              <div className="flex gap-2">
                <Button
                  onClick={handleAtribuirHonraria}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? "Atribuindo..." : "Atribuir"}
                </Button>
                <Button onClick={closeAtribuirModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Confirmar Remoção de Honraria */}
      {isRemoverModalOpen && militarHonrariaToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Remover Honraria
              </CardTitle>
              <CardDescription>
                Esta ação não pode ser desfeita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja remover a honraria{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {militarHonrariaToRemove.honrariaNome}
                  </span>{" "}
                  do militar{" "}
                  <span className="font-semibold">
                    {militarHonrariaToRemove.militarNick}
                  </span>
                  ?
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleRemoverHonraria}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Removendo..." : "Sim, Remover"}
                </Button>
                <Button onClick={closeRemoverModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Confirmar Exclusão de Honraria */}
      {isExcluirModalOpen && honrariaToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Excluir Honraria
              </CardTitle>
              <CardDescription>
                Esta ação não pode ser desfeita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja excluir permanentemente a honraria{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {honrariaToDelete.honraria}
                  </span>
                  ?
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Todos os militares que possuem esta honraria terão ela removida.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleDeleteHonraria(honrariaToDelete.id)}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Excluindo..." : "Sim, Excluir"}
                </Button>
                <Button onClick={closeExcluirModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
